import React, { useState, useEffect, useRef } from 'react';
import { MapPin, Search, ChevronDown, Check } from 'lucide-react';
import { partnerService } from '../../services/partnerManagement/partnerService';

const LocationDropdown = ({
    value,
    onChange,
    locations: initialLocations,
    valueKey = 'name', // 'name' or '_id'
    label,
    placeholder = 'Select Location',
    disabled = false,
    required = false,
    className = '',
    buttonClassName = '',
    error = '',
    searchPlaceholder = 'Search location...'
}) => {
    const [locations, setLocations] = useState(initialLocations || []);
    const [isOpen, setIsOpen] = useState(false);
    const [dropUp, setDropUp] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(false);
    const buttonRef = useRef(null);
    const selectedItemRef = useRef(null);

    const handleToggle = () => {
        if (!isOpen && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            if (spaceBelow < 240) {
                setDropUp(true);
            } else {
                setDropUp(false);
            }
        }
        setIsOpen(!isOpen);
    };

    useEffect(() => {
        if (initialLocations && initialLocations.length > 0) {
            setLocations(initialLocations);
        } else {
            fetchLocations();
        }
    }, [initialLocations]);

    // Scroll selected item into view whenever dropdown opens
    useEffect(() => {
        if (isOpen && selectedItemRef.current) {
            setTimeout(() => {
                selectedItemRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
            }, 50);
        }
    }, [isOpen]);

    const fetchLocations = async () => {
        try {
            setLoading(true);
            const data = await partnerService.getLocations();
            const locList = Array.isArray(data) ? data : data?.data || [];
            setLocations(locList);
        } catch (error) {
            console.error('Failed to fetch locations in LocationDropdown:', error);
        } finally {
            setLoading(false);
        }
    };

    // Find selected location object
    const selectedLocation = locations.find((loc) => {
        const val = typeof loc === 'object' ? loc[valueKey] || loc._id || loc.name : loc;
        return String(val) === String(value);
    });

    const getDisplayName = (loc) => {
        if (!loc) return '';
        if (typeof loc === 'string') return loc;
        return loc.name || loc.city || loc.location_name || '';
    };

    const displaySelectedText = selectedLocation ? getDisplayName(selectedLocation) : (value && valueKey === 'name' ? value : '');

    const filteredLocations = locations.filter((loc) => {
        const name = getDisplayName(loc);
        return name.toLowerCase().includes(searchQuery.toLowerCase());
    });

    const handleSelect = (loc) => {
        const selectedVal = typeof loc === 'object' ? loc[valueKey] || loc._id || loc.name : loc;
        if (onChange) {
            onChange(selectedVal, loc);
        }
        setIsOpen(false);
        setSearchQuery('');
    };

    return (
        <div className={`space-y-1.5 ${className}`}>
            {label && (
                <label className="block text-xs font-bold text-slate-700 tracking-wide mb-1.5">
                    {typeof label === 'string' && label.endsWith('*') ? (
                        <>
                            {label.slice(0, -1).trim()}{' '}
                            <span className="text-rose-500 ml-0.5">*</span>
                        </>
                    ) : (
                        <>
                            {label}
                            {required && <span className="text-rose-500 ml-0.5">*</span>}
                        </>
                    )}
                </label>
            )}

            <div className="relative">
                <button
                    ref={buttonRef}
                    type="button"
                    disabled={disabled}
                    onClick={handleToggle}
                    className={`w-full px-4 text-xs sm:text-sm h-[42px] rounded-xl border font-medium flex items-center justify-between transition-all bg-white cursor-pointer ${
                        isOpen
                            ? 'border-[#067978] ring-2 ring-[#067978]/20 text-slate-800 shadow-sm'
                            : error
                            ? 'border-rose-300 text-slate-700 hover:border-rose-400'
                            : 'border-slate-200 text-slate-700 hover:border-slate-300'
                    } ${disabled ? 'bg-[#067978]/5 cursor-not-allowed opacity-75' : ''} ${buttonClassName}`}
                >
                    <div className="flex items-center gap-2 truncate">
                        <MapPin size={16} className={displaySelectedText ? 'text-[#067978]' : 'text-slate-400'} />
                        <span className={displaySelectedText ? 'text-slate-700 font-medium' : 'text-slate-400 font-normal'}>
                            {displaySelectedText || placeholder}
                        </span>
                    </div>
                    <ChevronDown
                        size={16}
                        className={`text-slate-400 transition-transform duration-200 shrink-0 ${isOpen ? 'rotate-180 text-[#067978]' : ''}`}
                    />
                </button>

                {isOpen && !disabled && (
                    <>
                        <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
                        <div className={`absolute left-0 right-0 z-50 bg-white border border-slate-200 rounded-2xl shadow-xl overflow-hidden animate-in fade-in duration-150 ${
                            dropUp ? 'bottom-full mb-2 slide-in-from-bottom-2' : 'top-full mt-2 slide-in-from-top-2'
                        }`}>
                            {/* Fixed Opaque Search Header */}
                            <div className="p-2.5 bg-white border-b border-slate-100 z-10">
                                <div className="relative">
                                    <input
                                        type="text"
                                        placeholder={searchPlaceholder}
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className="w-full pl-8 pr-3 py-2 bg-slate-50 rounded-xl text-xs border border-slate-200 focus:outline-none focus:border-[#067978] focus:bg-white text-slate-700 font-medium"
                                        onClick={(e) => e.stopPropagation()}
                                        autoFocus
                                    />
                                    <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                                </div>
                            </div>

                            {/* Scrollable Items Container */}
                            <div className="max-h-48 overflow-y-auto p-1.5 space-y-1 bg-white scroll-smooth">
                                {loading ? (
                                    <div className="py-6 text-center text-xs text-slate-400 font-medium">
                                        Loading locations...
                                    </div>
                                ) : filteredLocations.length === 0 ? (
                                    <div className="py-6 text-center text-xs text-slate-400 font-medium">
                                        No locations found
                                    </div>
                                ) : (
                                    filteredLocations.map((loc) => {
                                        const name = getDisplayName(loc);
                                        const val = typeof loc === 'object' ? loc[valueKey] || loc._id || loc.name : loc;
                                        const isSelected = String(val) === String(value) || name === displaySelectedText;

                                        return (
                                            <button
                                                key={loc._id || val || name}
                                                ref={isSelected ? selectedItemRef : null}
                                                type="button"
                                                onClick={() => handleSelect(loc)}
                                                className={`w-full px-3 py-2.5 rounded-xl text-xs font-medium flex items-center justify-between transition-all text-left cursor-pointer ${
                                                    isSelected
                                                        ? 'bg-[#067978]/10 text-[#067978] font-semibold'
                                                        : 'text-slate-700 hover:bg-slate-50 hover:text-[#067978]'
                                                }`}
                                            >
                                                <span className="flex items-center gap-2">
                                                    <MapPin size={14} className={isSelected ? 'text-[#067978]' : 'text-slate-400'} />
                                                    {name}
                                                </span>
                                                {isSelected && <Check size={14} className="text-[#067978] stroke-[2.5]" />}
                                            </button>
                                        );
                                    })
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>

            {error && <p className="text-xs text-rose-500 font-medium mt-1">{error}</p>}
        </div>
    );
};

export default LocationDropdown;
